/**
 * @architecture-header 1
 * @layer module
 * @domain classrooms
 * @responsibility Handle class-wide guardian linking and the pre-send check.
 * @state-owner dataset store holds guardians; classroom UI store holds the channel
 * @allowed-imports React; dataset and workspace stores; classroom store; shared components and hooks
 * @forbidden direct API client
 * @public-api classrooms and reports modules
 * @side-effects guardian record updates and toasts
 * @tests workspace Playwright flows in frontend/apps/web/e2e/workspace.spec.ts
 */

import { ModalDocument, SectionLabel } from '../../../shared/components/Modal';
import { useOverlay } from '../../../shared/components/OverlayHost';
import { StatusIcon } from '../../../shared/components/StatusIcon';
import type { GuardianChannel } from '../../../shared/lib/model';
import { useDatasetStore } from '../../../shared/model/dataset-store';
import { useTranslator } from '../../../shared/model/use-workspace';
import { useWorkspaceUiStore } from '../../../shared/model/workspace-store';
import {
  CHANNEL_COLOR,
  CHANNEL_LABEL,
  GUARDIAN_STATE_LABEL,
  guardianState,
  guardianStats,
} from '../model/guardian';
import { useClassroomUiStore } from '../store/classroom-ui-store';
import { GuardianDialog } from './GuardianDialog';

const CHANNELS: readonly GuardianChannel[] = ['line', 'wa', 'wechat', 'sms'];

export function GuardianBatchDialog({ classIndex }: { classIndex: number }) {
  const t = useTranslator();
  const { closeModal, openModal } = useOverlay();
  const notify = useWorkspaceUiStore((state) => state.notify);
  const classes = useDatasetStore((state) => state.classes);
  const guardians = useDatasetStore((state) => state.guardians);
  const requests = useDatasetStore((state) => state.guardianRequests);
  const channel = useClassroomUiStore((state) => state.guardianChannel);
  const setChannel = useClassroomUiStore((state) => state.setGuardianChannel);

  const students = classes[classIndex].students;
  const stats = guardianStats(students, guardians);
  const missing = students.filter(
    (student) => guardianState(guardians[student] ?? []) !== 'linked',
  );

  const runWay = (key: 'roster' | 'sheet' | 'student') => {
    notify(
      t(
        key === 'roster'
          ? '已開啟名冊匯入，欄位: 學生姓名、家長姓名、關係、手機或 LINE ID'
          : key === 'sheet'
            ? '已產生全班邀請單 PDF，一人一張含 QR 與綁定碼'
            : '已在學生端開啟「邀請我的家長」',
      ),
    );
  };

  const sendAll = () => {
    const store = useDatasetStore.getState();
    for (const student of students) {
      if (guardianState(store.guardians[student] ?? []) !== 'none') continue;
      store.setGuardians(student, [
        { relation: t('家長'), channel, address: t('等待綁定'), state: 'pending' },
      ]);
    }
    closeModal();
    notify(`${t('已用')} ${t(CHANNEL_LABEL[channel])} ${t('送出邀請，家長確認後才算綁定')}`);
  };

  return (
    <ModalDocument
      footer={
        <>
          <div className="chrow">
            {CHANNELS.map((item) => (
              <button
                className={`chb2 ${channel === item ? 'on' : ''}`}
                key={item}
                style={{ ['--c' as string]: CHANNEL_COLOR[item] }}
                type="button"
                onClick={() => setChannel(item)}
              >
                {t(CHANNEL_LABEL[item])}
              </button>
            ))}
          </div>
          <div className="sp" />
          <button className="bt pri" disabled={!missing.length} type="button" onClick={sendAll}>
            {`${t('用')} ${t(CHANNEL_LABEL[channel])} ${t('邀請這')} ${missing.length} ${t('人')}`}
          </button>
        </>
      }
      maxWidth={560}
      subtitle={`${classes[classIndex].name} · ${stats.linked} / ${stats.total} ${t('已綁定')}`}
      title={t('全班家長綁定')}
    >
      <div className="gbar">
        <i style={{ width: `${(stats.linked / stats.total) * 100}%` }} />
        <i className="pd" style={{ width: `${(stats.pending / stats.total) * 100}%` }} />
      </div>
      <div className="gleg">
        <span>
          <StatusIcon glyph="done" /> {`${stats.linked} ${t('已綁定')}`}
        </span>
        <span>
          <StatusIcon glyph="half" /> {`${stats.pending} ${t('邀請待確認')}`}
        </span>
        <span>
          <StatusIcon glyph="warn" /> {`${stats.none} ${t('未綁定')}`}
        </span>
      </div>

      {requests.length ? (
        <button
          className="greqbar"
          type="button"
          onClick={() => openModal(<GuardianDialog student={requests[0].student} />)}
        >
          <b>{`${requests.length} ${t('筆家長請求等你確認')}`}</b>
          <span>{t('班級連結是公開的，一定要你確認過才算綁定')}</span>
        </button>
      ) : null}

      <SectionLabel>{t('三種收法，看你手上有什麼')}</SectionLabel>
      <div className="gways">
        <button className="gway" type="button" onClick={() => runWay('roster')}>
          <b>{t('匯入名冊')}</b>
          <span>{t('補習班報名資料本來就有家長手機，匯入後系統直接發邀請，老師零操作')}</span>
        </button>
        <button className="gway" type="button" onClick={() => runWay('sheet')}>
          <b>{t('列印邀請單')}</b>
          <span>{t('一人一張，含專屬 QR 與六位數綁定碼，讓學生帶回家')}</span>
        </button>
        <button className="gway" type="button" onClick={() => runWay('student')}>
          <b>{t('請學生自己邀請')}</b>
          <span>{t('學生端出現「邀請我的家長」，家長掃他手機上的 QR')}</span>
        </button>
      </div>

      <SectionLabel>
        {t('還沒綁定的')}{' '}
        <span className="vmut" style={{ fontWeight: 400 }}>{`${missing.length} ${t('人')}`}</span>
      </SectionLabel>
      {missing.length ? (
        <div className="glist">
          {missing.map((student) => (
            <div className="vgrow" key={student}>
              <span className="gav">{student[0]}</span>
              <span className="gt">
                <b>{student}</b>
                <span>{t(GUARDIAN_STATE_LABEL[guardianState(guardians[student] ?? [])][0])}</span>
              </span>
              <button
                className="bt sm"
                type="button"
                onClick={() => openModal(<GuardianDialog student={student} />)}
              >
                {t('單獨處理')}
              </button>
            </div>
          ))}
        </div>
      ) : (
        <div className="gexp">{t('全班都綁好了。')}</div>
      )}
    </ModalDocument>
  );
}

export function GuardianSendCheckDialog({
  names,
  onContinue,
}: {
  names: readonly string[];
  onContinue(): void;
}) {
  const t = useTranslator();
  const { closeModal, openModal } = useOverlay();
  const guardians = useDatasetStore((state) => state.guardians);
  const missing = names.filter((name) => guardianState(guardians[name] ?? []) !== 'linked');

  return (
    <ModalDocument
      footer={
        <>
          <div className="sp" />
          <button className="bt" type="button" onClick={closeModal}>
            {t('取消')}
          </button>
          <button
            className="bt pri"
            type="button"
            onClick={() => {
              closeModal();
              onContinue();
            }}
          >
            {t('先寄出可以寄的')}
          </button>
        </>
      }
      maxWidth={520}
      subtitle={`${names.length - missing.length} ${t('份可以寄出')} · ${missing.length} ${t('份沒有收件人')}`}
      title={t('有幾位還沒有家長綁定')}
    >
      <div className="gexp">{t('沒有綁定家長的學生，報告會先留著，等家長綁定之後再寄。')}</div>
      <div className="glist" style={{ marginTop: 12 }}>
        {missing.map((student) => (
          <div className="vgrow" key={student}>
            <span className="gav">{student[0]}</span>
            <span className="gt">
              <b>{student}</b>
              <span>{t(GUARDIAN_STATE_LABEL[guardianState(guardians[student] ?? [])][0])}</span>
            </span>
            <button
              className="bt sm"
              type="button"
              onClick={() => openModal(<GuardianDialog student={student} />)}
            >
              {t('產生家長邀請')}
            </button>
          </div>
        ))}
      </div>
    </ModalDocument>
  );
}
